import { useEffect, useRef, useState } from 'react'
import useTranslation from 'next-translate/useTranslation'

type Recognition = {
  lang: string
  continuous: boolean
  interimResults: boolean
  onstart: (() => void) | null
  onend: (() => void) | null
  onerror: ((event: { error: string }) => void) | null
  onresult:
    | ((event: {
        results: ArrayLike<ArrayLike<{ transcript: string }>>
      }) => void)
    | null
  start: () => void
  abort: () => void
}

type SpeechWindow = Window & {
  SpeechRecognition?: new () => Recognition
  webkitSpeechRecognition?: new () => Recognition
}

const MAX_QUERY_LENGTH = 500

const NO_SPEECH_TIMEOUT_MS = 4000

const SILENCE_TIMEOUT_MS = 2500

const getRecognitionApi = () => {
  const speechWindow = window as SpeechWindow

  return speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition
}

export const useDictation = (
  onText: (text: string) => void,
  locale: string,
  onFinish?: (text: string) => void
): {
  isSupported: boolean
  listening: boolean
  error: string
  start: (prefix: string) => void
  stop: () => void
  clearError: () => void
} => {
  const { t } = useTranslation('common')
  const [isSupported, setIsSupported] = useState(false)
  const [listening, setListening] = useState(false)
  const [error, setError] = useState('')
  const active = useRef<Recognition | null>(null)
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const callback = useRef(onText)
  callback.current = onText
  const finishCallback = useRef(onFinish)
  finishCallback.current = onFinish
  const transcript = useRef('')
  const noSpeechDeadline = useRef(0)

  const clearListeningTimeout = () => {
    if (timeout.current) {
      clearTimeout(timeout.current)
      timeout.current = null
    }
  }

  const scheduleStop = (delay: number) => {
    clearListeningTimeout()
    timeout.current = setTimeout(finish, delay)
  }

  const finish = () => {
    const text = transcript.current
    stop()

    if (text.trim()) {
      finishCallback.current?.(text)
    }
  }

  const stop = () => {
    clearListeningTimeout()
    const recognition = active.current
    active.current = null
    recognition?.abort()
    setListening(false)
  }

  useEffect(() => {
    setIsSupported(!!getRecognitionApi())

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        stop()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pagehide', stop)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pagehide', stop)
      clearListeningTimeout()
      const recognition = active.current
      active.current = null
      recognition?.abort()
    }
  }, [])

  const start = (prefix: string) => {
    if (active.current) {
      stop()
      return
    }

    setError('')
    transcript.current = ''
    noSpeechDeadline.current = 0
    const RecognitionApi = getRecognitionApi()

    if (!RecognitionApi) {
      return
    }

    const recognition = new RecognitionApi()
    active.current = recognition
    if (locale) {
      recognition.lang = locale
    }
    recognition.continuous = false
    recognition.interimResults = true

    recognition.onstart = () => {
      if (active.current !== recognition) {
        return
      }

      setListening(true)

      if (!noSpeechDeadline.current) {
        noSpeechDeadline.current = Date.now() + NO_SPEECH_TIMEOUT_MS
        scheduleStop(NO_SPEECH_TIMEOUT_MS)
      }
    }

    recognition.onresult = (event) => {
      if (active.current !== recognition) {
        return
      }

      scheduleStop(SILENCE_TIMEOUT_MS)

      const spoken = Array.from(event.results)
        .map((result) => result[0].transcript)
        .join(' ')

      transcript.current = [prefix.trim(), spoken.trim()]
        .filter(Boolean)
        .join(' ')
        .slice(0, MAX_QUERY_LENGTH)
      callback.current(transcript.current)
    }

    recognition.onerror = (event) => {
      // no-speech: prehliadač nič nepočul – reštart alebo koniec rieši onend
      if (active.current !== recognition || event.error === 'no-speech') {
        return
      }

      setError(
        event.error === 'not-allowed'
          ? t('Toolbar.AiSearch.voiceDenied')
          : t('Toolbar.AiSearch.voiceError')
      )
      transcript.current = ''
      noSpeechDeadline.current = 0
      setListening(false)
    }

    recognition.onend = () => {
      if (active.current !== recognition) {
        return
      }

      // prehliadač (hlavne mobilný Chrome) končí po ~1 s bez reči – kým nič
      // nezaznelo a náš limit nevypršal, počúvam ďalej
      if (!transcript.current && Date.now() < noSpeechDeadline.current) {
        try {
          recognition.start()

          return
        } catch {
          // reštart zlyhal – ukončím normálne
        }
      }

      finish()
    }

    try {
      recognition.start()
    } catch {
      clearListeningTimeout()
      active.current = null
      setListening(false)
      setError(t('Toolbar.AiSearch.voiceError'))
    }
  }

  return {
    isSupported,
    listening,
    error,
    start,
    stop,
    clearError: () => setError('')
  }
}
